import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Checkbox, Field, Icon } from '@/nova/components';
import { Sheet } from '@/nova/components/Sheet';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import { useAppDispatch, useAppSelector } from '@/store';
import { cancelJob, enqueueGeneration, selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { IngredientSection, SectionHint } from '../../../art-style/components/IngredientSection';
import { adLanguageLabel } from '../../ad-language';
import { adSubjectFromItem, promotionsForTracking, subjectLandingUrl } from '../../ad-subjects';
import { isLocked, versionKey } from '../../ad-versions';
import { fetchBannerKitCatalog } from '../../banner-kit-api';
import type { CreativeItem } from '../../creative-api';
import { useAdVersions } from '../../hooks/useAdVersions';
import { invalidateLandingDestinations } from '../../hooks/useLandingDestinations';
import type { SiteLanguagesState } from '../../hooks/useSiteLanguages';
import {
  canonicalTemplateId,
  cloneLandingParams,
  fetchLandingLegal,
  fetchLandingPage,
  fetchMarketingOffers,
  fetchLandingTemplates,
  PATH_TAKEN,
  castEqual,
  isCastNone,
  isImmersive,
  freeLandingSlug,
  isPublicationOf,
  kitImageModel,
  landingSceneBgModel,
  landingScenePrice,
  landingArtChanged,
  landingForkEntry,
  landingHeroNeedsReview,
  landingHeroUrl,
  landingHasArt,
  landingHoldsArt,
  landingCastCharacters,
  landingFeaturedGameArt,
  landingDefaultSlug,
  landingLetteringDefaultOn,
  landingOpDefaultOn,
  landingOpsAvailable,
  landingOpsCostFor,
  landingPreviewParams,
  landingRunTarget,
  landingScenePicksChanged,
  planLandingRun,
  previewLandingPage,
  primaryHost,
  publishLandingPage,
  resolveLandingTemplate,
  sceneHasArt,
  sceneLayerCount,
  siteLanguageList,
  spriteImageModel,
  unpublishLandingPage,
  upsertLandingPage,
  withImmersiveCopy,
  withoutGameLines,
  withInertLinks,
  withAutomaticCast,
  withSiteLanguages,
  isSceneBackgroundKey,
  landingScenePlan,
  landingSceneRedrawEntry,
  withoutSceneArt,
  type LandingFeatured,
  type LandingHeroQAReason,
  type LandingLegalInfo,
  type LandingOp,
  type LandingPage,
  type LandingPageUpsert,
  type LandingParams,
  type LandingPlanRequest,
  type LandingRunPlan,
  type LandingVersion,
  type LivePages,
} from '../../landing-page-api';
import { fetchPlayableTemplates, jsonEqual, type PlayableTemplate } from '../../playable-api';
import { isCancelled } from '../../storyboard';
import { detectWelcomeBonus, promotionName } from '../../welcome-bonus';
import { LINKS_SHEET_PARAM, STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import { AdArtStyleSection, AdCharacterSection, AdDrawnWith, useAdStyleRun } from '../AdArtStyle';
import { adStyleChoiceOf } from '../AdArtStyle/ad-art-style';
import { AdLanguageSection } from '../AdLanguageSection';
import { ComposerRailTabs, useRailEnter, type ComposerRailTab } from '../ComposerRailTabs';
import { FooterActions, FooterRow, SelCount } from '../GamePickerSheet/GamePickerSheet.styles';
import { GenerateButton } from '../GenerateButton';
import { NewVersionLink, useLockedNudge } from '../NewVersionLink';
import { PoolPickerSheet } from '../PoolPickerSheet';
import { TrackingLink, type TrackingLinkState } from '../TrackingLink';
import { VersionPicker, type VersionPickerItem } from '../VersionPicker';
import { AssetStatsSheet, StatsPill } from '../AssetStats';
import { LandingLinks } from './LandingLinks';
import { LandingPreview, type PreviewFailure } from './LandingPreview';
import { LandingTemplatePicker, type TemplatesRead } from './LandingTemplatePicker';
import {
  assetLinkPath,
  clearLinkFor,
  isLinkOf,
  isOwnLink,
  loadLinkFor,
  type LinkForContext,
} from './link-for';
import { sendLinkHere } from './link-for-send';
import { MadeForBar, type MadeForAsset, type MadeForState } from './MadeForBar';
import { usePublishControls, type LinksHere, type PublishFailure } from './PublishControls';
import { SceneCast, pickedCast } from './SceneCast';
import { SceneLayers } from './SceneLayers';
import { useLandingLinks } from './useLandingLinks';
import * as L from './LandingLinks.styles';
import * as S from './LandingPageComposer.styles';

export interface LandingPageComposerProps {
  tenantId: number;
  /** The landing page being edited (the library's live entry: its versions fill in as a generation lands). */
  page: LandingPage;
  brandName: string;
  /** The brand's site domain (a promotion's call-to-action link is built on it). */
  brandDomain?: string;
  /** The brand's promotion pool (the welcome offer, the featured promotion and the hero pickers). */
  items: CreativeItem[];
  /** The brand's live pages and landing hosts (null until read, or when the read failed). */
  live: LivePages | null;
  /** The live pages could not be read. */
  liveFailed: boolean;
  /** Optimistically merge fields into the library's cached page (a card and a reopen read them at once). */
  patch: (id: string, fields: Partial<LandingPage>) => void;
  /** The live pages changed (a publish or an unpublish): read them again. */
  onLiveChanged: () => void;
  onClose: () => void;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
  /** Go to another Studio page (an in-app path): an asset a link here belongs to, or back to the asset the page was
   *  made for. Without it those actions are not offered. */
  onNavigate?: (path: string) => void;
}

/** Pointing the made-for asset's link at the page (when this session holds a context not marked `linked`): not
 *  trying, on its way, or refused (the bar then offers Try again). */
type SendState = 'idle' | 'sending' | 'failed';

/** The rail's ingredient sections (one open at a time). */
type RailSection = 'subject' | 'language' | 'art-style' | 'character' | 'scene' | 'hero';

/** The brand website's legal footer and languages, as read. */
type LegalRead = { status: 'loading' } | { status: 'ready'; info: LandingLegalInfo } | { status: 'error' };

/** The subject picker's step: what kind of thing the page promotes, then that kind's picker; the welcome offer's own
 *  picker. */

interface PreviewState {
  /** The latest preview document (links inert); null before the first. */
  html: string | null;
  busy: boolean;
  failure: PreviewFailure | null;
}

/** spark-creatives' plan of a Generate, for the request it answers (`key`: the plan request's content; `opsKey`: its
 *  ops), or why it could not be read. */
type PlanRead =
  | { key: string; opsKey: string; status: 'ready'; plan: LandingRunPlan }
  | { key: string; opsKey: string; status: 'failed' };

/** The edits waiting for the debounced save: the document (with the draft it was made on, `at`) and the name. */
type PendingSave = { params?: LandingParams; name?: string; at?: string };

/** How long an edit rests before it is saved (one write per burst of typing). Not an animation timing. */
const SAVE_DEBOUNCE_MS = 700;
/** How long the preview (and the Generate's plan) waits after an edit before it asks again. A network bound, not an
 *  animation. */
const PREVIEW_DEBOUNCE_MS = 500;

const isActiveJob = (job: CreativeJob | undefined): boolean => job?.status === 'queued' || job?.status === 'running';

/** How long the editor waits for the page read that takes a finished run's document (the rail is held meanwhile). */
const TAKE_TIMEOUT_MS = 10000;

/** The generation to reflect: the one this session started, else the page's linked job, else a landing page run still
 *  in flight that the link missed (tagged with the page id as its root). Pure. */
export const landingRunJob = (
  pageId: string,
  jobId: string | undefined,
  jobsById: Record<string, CreativeJob>,
): CreativeJob | undefined => {
  const linked = jobId ? jobsById[jobId] : undefined;
  if (linked) return linked;
  return Object.values(jobsById).find(
    (j) => j.kind === 'landing_page' && j.root_banner_id === pageId && isActiveJob(j),
  );
};

/** An owner-readable reason a generation could not start (the backend's refusal text, matched loosely). Pure. */
const startErrorText = (err: unknown, t: TFunction): string => {
  const raw =
    typeof err === 'object' && err !== null && 'error' in err ? String((err as { error: unknown }).error) : '';
  const text = raw.toLowerCase();
  if (text.includes('not priced')) return t('Landing page generation is not available in this environment yet.');
  if (text.includes('template')) return t('Choose a template first.');
  return t("That couldn't be started. Please try again.");
};

/** The hero's "Needs review" tooltip: what the check actually found on the kept render (a literal `t()` per reason,
 *  so each is extracted). A hero checked before reasons were recorded gets the general line. Pure. */
const heroReviewReason = (reason: LandingHeroQAReason | undefined, t: TFunction): string => {
  switch (reason) {
    case 'text':
      return t("Lumio's check found lettering on this hero. Look it over, or generate it again.");
    case 'logo':
      return t("Lumio's check found what may be a logo on this hero. Look it over, or generate it again.");
    case 'numbers':
      return t("Lumio's check found numbers on this hero. Look it over, or generate it again.");
    case 'unavailable':
      return t("Lumio's check could not run on this hero. Look it over before you use it.");
    default:
      return t(
        "Lumio's check could not confirm this hero is free of text, logos and numbers. Look it over, or generate it again.",
      );
  }
};

/**
 * Marketing Assets > Landing Pages > a saved page. A new page (its subject picked first, in the library) opens on its
 * TEMPLATE STEP (a full-screen game, wheel or pick a prize, or the regular offer page; each card a live mini-preview
 * drawn in the Studio's colours); once chosen, the template never changes. The rail (the Art Style page's ingredient sections, one open at a time) holds
 * what the owner decides: the subject (what the page promotes: one promotion or up to four games, else the welcome
 * offer itself; the welcome promotion stays attached as the page's button and offer), the art style and the lead
 * character the next run draws with (run options, seeded from the newest version). The marketing offer and the button
 * link are set automatically (the brand's default offer, the promotion's page on its site). Everything else is Lumio's
 * or the website's: the copy (every website language, kept in sync), the scene, the
 * lettering, the fonts, the payment icons and the legal footer. Scene layers, the cast and the Generate's op ticks
 * show only with the "777devdebug" Lumio code (the creatives-dev-debug flag); without it Generate runs on the
 * defaults. On the right, the page as the server renders it (a game playable in it), redrawn shortly after each edit,
 * with its Publish panel and, once live, its own tracking link; under it the ads' links routed through the page (each
 * opens its asset) and the combined results of every link landing here. A page made for an asset's link (its URL
 * carried the asset, see link-for.ts) says so on top and, once published, points that link here and offers the way
 * back.
 *
 * Versions (the video ads' rule): Generate (its cost from spark-creatives' plan) fills the selected draft (or a new
 * version) and LOCKS it. A generated version is read-only: its rail is disabled and its foot offers "New version from
 * this" in place of Generate (a click on a disabled field shakes it); the new draft keeps the art while nothing that
 * draws it changes. Publishing puts the selected version live at `https://lp.<domain>/<path>`.
 */
export const LandingPageComposer = ({
  tenantId,
  page,
  brandName,
  brandDomain,
  items,
  live,
  liveFailed,
  patch,
  onLiveChanged,
  onClose,
  onBuyCredits,
  onNotify,
  onNavigate,
}: LandingPageComposerProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const gate = useFeatureGate();
  // The Scene section and the Generate's op ticks are developer controls ("777devdebug"); without them Generate runs on
  // the defaults.
  const devDebug = gate.isEnabled('creatives-dev-debug');
  const pricing = useAppSelector((state) => state.aiCredits?.pricing);
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const notifyRef = React.useRef(onNotify);
  notifyRef.current = onNotify;

  /* ── Reads (each once per open sheet) ── */

  const [templates, setTemplates] = React.useState<TemplatesRead>({ status: 'loading' });
  // The mini-game registry is read only for the image models its prices name (the scene's backgrounds and cut-outs
  // are priced per model).
  const [registry, setRegistry] = React.useState<PlayableTemplate[]>([]);
  React.useEffect(() => {
    let alive = true;
    void fetchLandingTemplates().then((list) => {
      if (alive) setTemplates(list ? { status: 'ready', list } : { status: 'error' });
    });
    void fetchPlayableTemplates().then((r) => {
      if (alive && r) setRegistry(r.templates);
    });
    return () => {
      alive = false;
    };
  }, []);

  const [legal, setLegal] = React.useState<LegalRead>({ status: 'loading' });
  React.useEffect(() => {
    const controller = new AbortController();
    void fetchLandingLegal(tenantId, controller.signal).then((res) => {
      if (controller.signal.aborted) return;
      setLegal(res.ok ? { status: 'ready', info: res.data } : { status: 'error' });
    });
    return () => controller.abort();
  }, [tenantId]);

  const templateList = React.useMemo(() => (templates.status === 'ready' ? templates.list : []), [templates]);
  /** A template id as saved: a retired id becomes its successor's (an unknown one stays as it is). */
  const canon = React.useCallback((id: string): string => canonicalTemplateId(templateList, id), [templateList]);

  /* ── Saving: one ordered write chain, edits debounced ── */

  const writeChainRef = React.useRef<Promise<unknown>>(Promise.resolve());
  const pendingRef = React.useRef<PendingSave>({});
  const saveTimerRef = React.useRef<number | null>(null);
  // Whether a version is saved (set once the versions hook is up, below): a write naming a draft waits for it.
  const persistedOfRef = React.useRef<(key: string) => Promise<boolean>>(() => Promise.resolve(true));

  /** Queue one upsert after every earlier one (a slow write never lands over a newer one). A write naming a draft
   *  waits for that draft's own save: one the backend does not have would take nothing, so the document then goes to
   *  the page alone. */
  const write = React.useCallback(
    (body: Omit<LandingPageUpsert, 'id'>): Promise<boolean> => {
      const run = writeChainRef.current
        .then(async () => {
          let send = body;
          const draftKey = send.ad_version_at && !send.ad_version ? versionKey(send.ad_version_at) : undefined;
          if (draftKey && !(await persistedOfRef.current(draftKey))) {
            const { ad_version_at: _drop, ...rest } = send;
            send = rest;
          }
          const res = await upsertLandingPage(tenantId, { id: page.id, ...send });
          return res.ok;
        })
        .catch(() => false);
      writeChainRef.current = run;
      return run;
    },
    [tenantId, page.id],
  );

  /** Take the debounced edits out of the queue (a caller writes them itself, or drops them). */
  const takePending = React.useCallback((): PendingSave => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = {};
    return pending;
  }, []);

  const flush = React.useCallback((): Promise<boolean> => {
    const { params: p, name: n, at } = takePending();
    const body: Omit<LandingPageUpsert, 'id'> = {
      ...(p ? { ad_params: p, ...(at ? { ad_version_at: at } : {}) } : {}),
      ...(n && n.trim() ? { name: n.trim() } : {}),
    };
    if (!body.ad_params && !body.name) return Promise.resolve(true);
    return write(body).then((ok) => {
      if (!ok) notifyRef.current(t("Your changes couldn't be saved. Please try again."));
      return ok;
    });
  }, [takePending, write, t]);

  const schedule = React.useCallback(
    (fields: PendingSave): void => {
      // An edit on another draft than the one waiting: the waiting edits are written to their own draft first.
      const waiting = pendingRef.current;
      if (fields.params !== undefined && waiting.params !== undefined && waiting.at !== fields.at) void flush();
      pendingRef.current = { ...pendingRef.current, ...fields };
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  // Closing the composer (or a brand switch unmounting it) never drops an edit still resting in the debounce.
  const flushRef = React.useRef(flush);
  flushRef.current = flush;
  React.useEffect(
    () => () => {
      void flushRef.current();
    },
    [],
  );

  /* ── The document and its versions ── */

  const [name, setName] = React.useState(page.name);
  const nameRef = React.useRef<HTMLInputElement>(null);
  // The performance of every link landing here, its own included (the header's Stats pill).
  // The stats sheet is the address's (`?stats=<id>`): Back from one of its deep links reopens it.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  const statsOpen = statsSheet.value === page.id;
  // The editor opens on the newest version's own document (a draft owns its document).
  const [params, setParams] = React.useState<LandingParams>(() => page.ad_versions[0]?.params ?? page.ad_params);
  const paramsRef = React.useRef(params);

  const { versions, selected, select, current, fork, forkIfLocked, mintTarget, patchVersion, persistedOf } =
    useAdVersions<LandingVersion>({
      versions: page.ad_versions,
      entityId: page.id,
      tenantId,
      persistFork: (entry, at) =>
        upsertLandingPage(tenantId, { id: page.id, ad_version: entry, ad_version_at: at }).then((res) => {
          if (!res.ok) throw new Error(res.error || 'fork not saved');
          return res.data;
        }),
      patchEntity: (next) => patch(page.id, { ad_versions: next }),
      onForkLost: (restored) => {
        notifyRef.current(t("Your changes couldn't be saved. Please try again."));
        if (restored) {
          paramsRef.current = restored.params;
          setParams(restored.params);
          const { name: waitingName } = takePending();
          if (waitingName !== undefined) schedule({ name: waitingName });
        }
      },
    });
  persistedOfRef.current = persistedOf;

  // A page with no version gets its first (empty) one at once, so every edit and the first Generate land on it.
  const ensuredRef = React.useRef(false);
  const hasVersions = versions.length > 0;
  React.useEffect(() => {
    if (ensuredRef.current || hasVersions) return;
    ensuredRef.current = true;
    fork({ params: cloneLandingParams(paramsRef.current), assets: {} });
  }, [hasVersions, fork]);

  // A generated (locked) version's document is what it was generated with: a run writes the copy and the scene into
  // it, so the editor shows the version's own document once it lands.
  React.useEffect(() => {
    if (!selected || !isLocked(selected) || jsonEqual(paramsRef.current, selected.params)) return;
    paramsRef.current = selected.params;
    setParams(selected.params);
  }, [selected]);
  // A run that leaves its draft open (a copy-only run: spark-creatives does not lock it) wrote the draft's document on
  // the server: the editor takes that document once the run is done (below). An edit made since drops the pending
  // take (applyEdit), so a late answer can never undo it.
  const adoptRunRef = React.useRef<string | undefined>(undefined);
  // While that document is being read, the automatic fills (website languages, offer, button link, welcome promotion)
  // hold (takingRun, and the armed take itself for the render the run ends in): they go through the owner's edit path,
  // which would drop the pending take and save the pre-run document over the run's words. They run once the take has
  // resolved, on the document it took. Generate waits for it too.
  const [takingRun, setTakingRun] = React.useState(false);

  /* ── The run's art style and lead character (run options, never saved in the document) ── */

  // Seeded from the newest version (its style while the brand still lists it, and the character picked for it).
  const styleRun = useAdStyleRun(tenantId, page.ad_versions[0]?.art_style);
  // The picked style's cast a scene can feature (its mascots with an image).
  const castMascots = styleRun.cast;

  /* ── The run ── */

  const [sessionJobId, setSessionJobId] = React.useState<string | undefined>(undefined);
  const job = landingRunJob(page.id, sessionJobId ?? page.job_id, jobsById);
  const active = isActiveJob(job);
  const [runTargetKey, setRunTargetKey] = React.useState<string | undefined>(undefined);
  const runKey = active
    ? runTargetKey && versions.some((v) => v.key === runTargetKey && !isLocked(v))
      ? runTargetKey
      : versions.find((v) => !isLocked(v))?.key
    : undefined;
  // The draft a run fills, while it runs. When the run ends done and leaves it open, its document is read from the page
  // itself (the cached page may still hold the editor's pre-run document: keepPendingVersions) and put on the cached
  // page; the editor takes it when that draft is the one on screen. The take stays armed (adoptRunRef) until it
  // resolves, so a re-run of this effect reads again instead of dropping it. A locked version is the generated-version
  // sync's (above).
  const lastRunKeyRef = React.useRef<string | undefined>(undefined);
  const jobStatus = job?.status;
  React.useEffect(() => {
    if (active) {
      lastRunKeyRef.current = runKey;
      return undefined;
    }
    if (lastRunKeyRef.current && jobStatus === 'done') adoptRunRef.current = lastRunKeyRef.current;
    lastRunKeyRef.current = undefined;
    const key = adoptRunRef.current;
    if (!key) return undefined;
    setTakingRun(true);
    const controller = new AbortController();
    // A read that never answers must not hold the rail: past TAKE_TIMEOUT_MS the take is given up (the editor keeps
    // its document, as on a failed read).
    let timedOut = false;
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, TAKE_TIMEOUT_MS);
    void fetchLandingPage(tenantId, page.id, controller.signal).then((res) => {
      window.clearTimeout(timer);
      if (controller.signal.aborted && !timedOut) return;
      setTakingRun(false);
      if (adoptRunRef.current !== key) return;
      adoptRunRef.current = undefined;
      if (timedOut || !res.ok) return;
      const ran = res.data.ad_versions.find((v) => versionKey(v.created_at) === key);
      if (!ran || isLocked(ran)) return;
      patchVersion(key, ran.params);
      if (current()?.key !== key || jsonEqual(paramsRef.current, ran.params)) return;
      paramsRef.current = ran.params;
      setParams(ran.params);
    }).catch(() => {
      // Never leave the rail held on a read that threw: the take is given up, as on a failed read.
      window.clearTimeout(timer);
      if (controller.signal.aborted && !timedOut) return;
      if (adoptRunRef.current === key) adoptRunRef.current = undefined;
      setTakingRun(false);
    });
    return () => {
      window.clearTimeout(timer);
      controller.abort();
      setTakingRun(false);
    };
  }, [active, runKey, jobStatus, tenantId, page.id, patchVersion, current]);
  const [submitting, setSubmitting] = React.useState(false);
  const [stopping, setStopping] = React.useState(false);
  const [pubBusy, setPubBusy] = React.useState<'publish' | 'unpublish' | null>(null);
  // No edits while a generation is starting or in flight (it writes the document saved at its start), nor while its
  // finished document is being taken (takingRun), nor while a
  // publish reads the version.
  const formLocked = active || takingRun || submitting || pubBusy !== null;
  // A generated version is read-only: "New version from this" is the one way to change it (Publish still works).
  const lockedView = !active && isLocked(selected);
  const railLocked = formLocked || lockedView;
  // The automatic fills wait while the rail is locked, and while a finished run's document is being taken (above).
  const fillsHeld = railLocked || takingRun;
  const lockedNudge = useLockedNudge(lockedView, selected?.key);

  /* ── Edits ── */

  /** Apply an edit to the draft it is made on, and save it there and on the page. A generated version takes none (the
   *  rail is disabled; "New version from this" starts a draft). A draft holding art that the edit would contradict
   *  (another template, promotion, featured subject or art note) moves the edit to a new version without that art. */
  const applyEdit = React.useCallback(
    (next: LandingParams): void => {
      adoptRunRef.current = undefined;
      const sel = current();
      if (sel && isLocked(sel)) return;
      if (sel && landingHoldsArt(sel) && landingArtChanged(sel.params, next, canon)) {
        fork({ params: withoutSceneArt(cloneLandingParams(next)), assets: {} });
      }
      const draft = current();
      const onDraft = draft && !isLocked(draft) ? draft : undefined;
      const doc = onDraft && onDraft.key !== sel?.key ? onDraft.params : next;
      if (onDraft) patchVersion(onDraft.key, doc);
      paramsRef.current = doc;
      setParams(doc);
      patch(page.id, { ad_params: doc });
      schedule({ params: doc, at: onDraft?.created_at });
    },
    [current, fork, patchVersion, patch, page.id, schedule, canon],
  );
  /** An owner's edit of the document as it is NOW (an edit earlier in the same event included); none while the rail
   *  is locked, and none when it changes nothing. A page on a retired template id is saved with its successor's. */
  const edit = (fn: (cur: LandingParams) => LandingParams): void => {
    if (railLocked) return;
    const cur = paramsRef.current;
    const next = fn(cur);
    if (next === cur) return;
    const id = canon(next.template_id);
    applyEdit(id === next.template_id ? next : { ...next, template_id: id });
  };
  const editRef = React.useRef(edit);
  editRef.current = edit;

  const onName = (value: string): void => {
    setName(value);
    if (!value.trim()) return; // a blank name is "not provided": keep the saved one
    patch(page.id, { name: value.trim() });
    schedule({ name: value });
  };

  const onSelectVersion = (key: string): void => {
    const v = versions.find((x) => x.key === key);
    if (!v || pubBusy) return;
    void flush();
    select(key);
    paramsRef.current = v.params;
    setParams(v.params);
    // The ops start on what that version holds, and its art's style and character on what it was drawn with.
    setOpPicks({});
    setSceneRedrawAll(false);
    if (landingHoldsArt(v) && v.art_style) styleRun.setChoice(adStyleChoiceOf(v.art_style));
  };

  /** Start a new (unlocked) version from the generated one on screen: it keeps the art and the lettering, so the next
   *  Generate draws only what an edit changes. */
  const newVersion = (): void => {
    const sel = current();
    if (!sel || !isLocked(sel) || formLocked) return;
    void flush();
    if (!forkIfLocked(landingForkEntry(sel, cloneLandingParams(sel.params), canon))) {
      onNotify(t("That couldn't be started. Please try again."));
      return;
    }
    setOpPicks({});
    setSceneRedrawAll(false);
  };

  /* ── Rail state ── */

  const [open, setOpen] = React.useState<RailSection | null>('subject');
  const toggle = (id: RailSection): void => setOpen((cur) => (cur === id ? null : id));
  // On a phone the two rails are tabs (Ingredients / Landing page); Generate from Ingredients shows the page. Until the
  // owner picks one the tab follows the version shown: one with a generated page opens on it, one with nothing
  // generated yet on its Ingredients (the video composer's rule).
  const [pickedRailTab, setRailTab] = React.useState<ComposerRailTab | null>(null);
  const railTab: ComposerRailTab =
    pickedRailTab ?? (isLocked(selected) || landingHoldsArt(selected) ? 'output' : 'ingredients');
  const railEnter = useRailEnter(railTab, pickedRailTab !== null);
  const [pickingHero, setPickingHero] = React.useState(false);
  const [heroPick, setHeroPick] = React.useState<string | null>(null);

  // The page's template, by its id or a retired id it replaced.
  const template = resolveLandingTemplate(templateList, params.template_id);
  // A full-screen game: the template IS the game.
  const immersive = isImmersive(template);
  // The template step: only a draft with no template (a new page) or one the catalogue no longer has. Once chosen, a
  // page's template never changes (the user: "once a LP type was chosen, we should not switch"); a generated version
  // stays in the composer (its preview says the template is gone; a new version from it picks one).
  const showPicker = !lockedView && (!params.template_id || (templates.status === 'ready' && !template));

  // The page is on every language of its brand website, the one it opens in first (the owner's opening language while
  // the website has it, else the website's default): a draft follows the website's list.
  const siteLanguages = legal.status === 'ready' ? legal.info.languages : null;
  const siteDefault = legal.status === 'ready' ? legal.info.default_language : '';
  const pageLanguages = params.languages.join(',');
  // A language added to a full-screen game gets the game's copy seeded too, so the sync waits for the catalogue.
  const templatesReading = Boolean(params.template_id) && templates.status === 'loading';
  React.useEffect(() => {
    // Syncs the draft with the brand website's languages (read from spark-creatives); a generated version is left as
    // it is (it is never changed in place).
    if (!siteLanguages || fillsHeld || adoptRunRef.current || templatesReading) return;
    editRef.current((cur) => withSiteLanguages(cur, siteLanguages, siteDefault, template));
  }, [
    siteLanguages,
    siteDefault,
    fillsHeld,
    templatesReading,
    template,
    pageLanguages,
    params.default_language,
    params.opening_language,
    selected?.key,
  ]);
  // The Language section's options: the website's languages as the composer read them (the sync above reads the same).
  const siteLanguagesState = React.useMemo<SiteLanguagesState>(() => {
    if (legal.status !== 'ready') return { status: legal.status, languages: [], defaultLanguage: '', known: false };
    const languages = siteLanguageList(legal.info.languages, legal.info.default_language);
    return {
      status: 'ready',
      languages,
      defaultLanguage: languages[0] ?? '',
      known: legal.info.languages_known && languages.length > 0,
    };
  }, [legal]);
  /** The owner picks the language the page opens in: it keeps every website language, the picked one first. Not an art
   *  input (the lettering follows the copy), so a draft holding art keeps it. */
  const onOpeningLanguage = (language: string): void => {
    if (!siteLanguages) return;
    edit((cur) =>
      cur.opening_language === language
        ? cur
        : withSiteLanguages({ ...cur, opening_language: language }, siteLanguages, siteDefault, template),
    );
  };

  // The page's offer and button link are set automatically (no field asks for them). A draft created while the offers
  // or the brand's domain could not be read gets them here once they can be: a failed read at creation is never the
  // answer the page keeps. Only an empty one is filled; a generated version is left as it is.
  React.useEffect(() => {
    // Syncs with spark-creatives: a draft with no offer gets the brand's default active marketing offer.
    if (fillsHeld || adoptRunRef.current || params.offer.moid || !tenantId) return undefined;
    const controller = new AbortController();
    void fetchMarketingOffers(tenantId, controller.signal).then((res) => {
      if (controller.signal.aborted || adoptRunRef.current || !res.ok || !res.data.available) return;
      const list = res.data.offers;
      const pick = list.find((o) => o.is_default && o.is_active) ?? list.find((o) => o.is_active);
      if (!pick) return;
      editRef.current((cur) => (cur.offer.moid ? cur : { ...cur, offer: { moid: pick.moid, name: pick.name } }));
    });
    return () => controller.abort();
  }, [tenantId, fillsHeld, params.offer.moid, selected?.key]);
  React.useEffect(() => {
    // The button's link: the promotion's page on the brand's site, once the brand's domain is known.
    if (fillsHeld || adoptRunRef.current || params.cta_url || !brandDomain) return;
    const url = subjectLandingUrl(params.subject, brandDomain);
    if (url) editRef.current((cur) => (cur.cta_url ? cur : { ...cur, cta_url: url }));
  }, [fillsHeld, params.cta_url, params.subject, brandDomain, selected?.key]);
  // The page's subject is the welcome promotion it claims. A draft created while the promotion pool could not be read
  // has none: it gets it here once the pool has it, with the button's link, which then went to the site's home. A draft
  // holding art keeps it as it is: the subject is part of what the art was drawn from, so the edit would fork the art
  // away.
  const welcome = React.useMemo(() => detectWelcomeBonus(items), [items]);
  React.useEffect(() => {
    // Syncs with the promotion pool (read from the casino): only an empty subject, and only on a draft with no art.
    if (fillsHeld || adoptRunRef.current || params.subject.id || !welcome || landingHoldsArt(current())) return;
    const subject = adSubjectFromItem(welcome);
    editRef.current((cur) => {
      if (cur.subject.id) return cur;
      const homeLink = subjectLandingUrl(cur.subject, brandDomain);
      const cta_url =
        !cur.cta_url || cur.cta_url === homeLink ? subjectLandingUrl(subject, brandDomain) || cur.cta_url : cur.cta_url;
      return { ...cur, subject, cta_url };
    });
  }, [fillsHeld, params.subject.id, welcome, brandDomain, selected?.key, current]);

  // The promotions with art (one card per promotion, though it may run in several places).
  const artItems = React.useMemo(() => {
    const seen = new Set<string>();
    return items.filter((item) => {
      if (!item.image_url || seen.has(item.banner_id)) return false;
      seen.add(item.banner_id);
      return true;
    });
  }, [items]);

  const pickTemplate = (id: string): void => {
    edit((cur) => {
      if (canon(cur.template_id) === id) return cur;
      const tpl = templateList.find((x) => x.id === id);
      const prev = resolveLandingTemplate(templateList, cur.template_id);
      // A full-screen game's own copy (prizes, call to play, win modal) is seeded where it is empty. The lines that
      // name the game ("Spin the wheel...", "The wheel is illustrative") are cleared when the game changes, so the new
      // game's own words are seeded or rendered instead of the old one's.
      if (isImmersive(tpl)) {
        const base = prev?.game && prev.game !== tpl?.game ? withoutGameLines(cur) : cur;
        return withImmersiveCopy({ ...base, template_id: id }, tpl?.game);
      }
      return { ...cur, template_id: id };
    });
  };

  const confirmHero = (): void => {
    const item = artItems.find((i) => i.banner_id === heroPick);
    if (item?.image_url) {
      const url = item.image_url;
      edit((cur) => ({ ...cur, hero: { ...cur.hero, image_url: url } }));
    }
    setPickingHero(false);
  };

  /* ── Generate / Stop ── */

  // The kit's Flare model (the banner-kit catalogue), which scene backgrounds draw on for a style that is not mature.
  const [flareModel, setFlareModel] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    let alive = true;
    void fetchBannerKitCatalog().then((catalog) => {
      if (alive) setFlareModel(catalog?.flare_model);
    });
    return () => {
      alive = false;
    };
  }, []);
  const available = landingOpsAvailable(params, template);
  // The owner's ticks (developer controls only; unset: the op's default for the version on screen, see
  // landingOpDefaultOn). Kept through an edit, cleared when another version is picked or a run starts.
  const [opPicks, setOpPicks] = React.useState<Partial<Record<LandingOp, boolean>>>({});
  // The owner asked Scene art to draw the whole scene again rather than only what the version is missing (developer
  // control only).
  const [sceneRedrawAll, setSceneRedrawAll] = React.useState(false);
  // The run draws in another style or with another lead character than the version's scene was drawn with: its scene
  // is drawn again whole (art from other picks is never filled in around).
  // Without the developer controls the scene's cast is automatic, so the lead character pick draws the first one.
  const runParams = devDebug ? params : withAutomaticCast(params);
  const picksChanged = landingScenePicksChanged(selected, {
    styleId: styleRun.ready ? styleRun.styleId : undefined,
    mascotPick: styleRun.mascotPick,
    cast: runParams.cast,
  });
  const redrawAll = picksChanged || (devDebug && sceneRedrawAll);
  // What Scene art draws on the version on screen for the page's cast: only what it misses (the service's plan), or
  // the whole scene.
  const scenePlan = landingScenePlan({
    params: runParams,
    assets: selected?.assets,
    template,
    styleCast: castMascots.length,
    redrawAll,
  });
  const sceneCanFill =
    scenePlan.recorded &&
    sceneHasArt(params.scene) &&
    scenePlan.missing.length > 0 &&
    scenePlan.missing.length < scenePlan.all.length;
  // spark-creatives' plan of the Generate on screen: its cost, and whether some lettering is missing or stale.
  const [planRead, setPlanRead] = React.useState<PlanRead | null>(null);
  const lastPlan = planRead?.status === 'ready' ? planRead.plan : undefined;
  const opOn = (op: LandingOp): boolean => {
    // Without the developer controls, Generate runs on the defaults (ticks made with them are not kept).
    const pick = devDebug ? opPicks[op] : undefined;
    if (pick !== undefined) return pick;
    if (op === 'scene') return scenePlan.defaultOn || picksChanged;
    // The lettering is drawn again with the copy, else when the plan says some of it no longer reads the copy.
    if (op === 'lettering')
      return landingLetteringDefaultOn(available.includes('copy') && opOn('copy'), lastPlan?.lettering_stale);
    return landingOpDefaultOn(op, params, selected?.assets, template);
  };
  const ops = available.filter(opOn);
  // What the lettering draws follows the style too (a mature style draws on another model).
  const draws = ops.some((op) => op === 'hero' || op === 'scene' || op === 'lettering');
  // No price until the art style is read when the run draws (what it draws follows the style).
  const kitModel = React.useMemo(() => kitImageModel(registry), [registry]);
  const spriteModel = React.useMemo(() => spriteImageModel(registry), [registry]);
  // Backgrounds draw on Flare unless the PICKED style is mature (then Seedream), so they are priced at that model's row.
  const bgModel = landingSceneBgModel(kitModel, flareModel, styleRun.mature, pricing);
  const runStyleId = styleRun.sendStyleId;
  const runMascotId = styleRun.mascotPick || undefined;

  // The plan is asked for the run as Generate sets it up (the document it saves, the art of the version it fills, its
  // style and lead character); with nothing ticked, for the lettering alone (its staleness still decides its tick).
  const planOps: LandingOp[] = ops.length > 0 ? ops : ['lettering'];
  const planOpsKey = planOps.join(',');
  const planTarget = landingRunTarget({
    params: { ...runParams, template_id: canon(params.template_id) },
    version: selected,
    ops: planOps,
    sceneFull: scenePlan.full,
    canon,
  });
  const planBody: LandingPlanRequest = {
    landing_page_id: page.id,
    ops: planOps,
    params: planTarget.params,
    assets: planTarget.assets,
    ...(runStyleId ? { style_id: runStyleId } : {}),
    ...(runMascotId ? { mascot_id: runMascotId } : {}),
  };
  // The request's content: a new plan is asked for only when it changes (none without a template, during a run, on a
  // generated version, or before the art style is read).
  const planKey = template && !active && !lockedView && styleRun.ready ? JSON.stringify(planBody) : '';
  const planBodyRef = React.useRef(planBody);
  planBodyRef.current = planBody;
  React.useEffect(() => {
    // Syncs with spark-creatives' planner: shortly after the last edit or tick, ask what the Generate would draw.
    if (!planKey) return undefined;
    const body = planBodyRef.current;
    const opsKey = body.ops.join(',');
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void planLandingRun(tenantId, body, controller.signal).then((res) => {
        if (controller.signal.aborted || (!res.ok && res.aborted)) return;
        setPlanRead(
          res.ok
            ? { key: planKey, opsKey, status: 'ready', plan: res.data }
            : { key: planKey, opsKey, status: 'failed' },
        );
      });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [tenantId, planKey]);
  const planNow = planRead && planRead.key === planKey ? planRead : undefined;
  // The front end's own estimate, for when the plan cannot be read (none with the lettering ticked: only the plan
  // prices it).
  const estimate = landingOpsCostFor(ops, pricing, kitModel, { spriteModel, template, bgModel, keys: scenePlan.keys });
  // The plan's total; while it is asked again, the last answer for the same ticks (an edit rarely moves it).
  const planCost =
    planNow?.status === 'ready'
      ? planNow.plan.total_credits
      : !planNow && planRead?.status === 'ready' && planRead.opsKey === planOpsKey
        ? planRead.plan.total_credits
        : estimate;
  const cost = ops.length > 0 && (!draws || styleRun.ready) ? planCost : undefined;
  // The plan finds nothing to draw (everything ticked is already held): Generate would be refused.
  const nothingToDraw =
    ops.length > 0 && planNow?.status === 'ready' && planNow.plan.keys.length === 0 && planNow.plan.total_credits === 0;
  // A run of the lettering alone waits for the plan of what is on screen: with nothing stale it draws nothing, and
  // spark-creatives refuses it.
  const awaitsPlan = ops.length === 1 && ops[0] === 'lettering' && planNow === undefined;
  // The scene is priced per image (each background, each cut-out), shown under the ops.
  const scenePrice = landingScenePrice(pricing, bgModel, spriteModel, template);
  // Any run may start: a template, nothing starting or in flight, an unlocked version on screen, no publish under way.
  const runIdle = Boolean(template) && !active && !takingRun && !submitting && !lockedView && pubBusy === null;
  const canGenerate = runIdle && ops.length > 0 && !nothingToDraw && !awaitsPlan && (!draws || styleRun.ready);
  // Some lettering is missing or no longer reads the copy (the page shows the live text there until it is drawn).
  const letteringStale = lastPlan?.lettering_stale === true;
  const hasLettering = Object.keys(params.lettering).length > 0;

  /** Start a generation of `runOps` (the Generate's ops by default) when `allowed` (the Generate's `canGenerate` by
   *  default). */
  const startRun = async (runOps: readonly LandingOp[] = ops, allowed: boolean = canGenerate): Promise<void> => {
    if (!allowed || runOps.length === 0) return;
    const styleId = runStyleId;
    const mascotId = runMascotId;
    setSubmitting(true);
    try {
      // A page on a retired template id runs (and is saved) as its successor; without the developer controls, on the
      // automatic cast.
      const held = paramsRef.current;
      const live = devDebug ? held : withAutomaticCast(held);
      const liveId = canon(live.template_id);
      const doc = liveId === live.template_id ? live : { ...live, template_id: liveId };
      if (doc !== held) {
        paramsRef.current = doc;
        setParams(doc);
      }
      // The run writes into the SAVED document: write it now (the debounce may still hold the last edit).
      const { name: pendingName } = takePending();
      const editing = current();
      const draft = editing && !isLocked(editing) ? editing : undefined;
      if (draft) patchVersion(draft.key, doc);
      const saved = await write({
        ad_params: doc,
        ...(draft ? { ad_version_at: draft.created_at } : {}),
        ...(pendingName?.trim() ? { name: pendingName.trim() } : {}),
      });
      if (current()?.key !== editing?.key) return;
      if (!saved) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      // A generated version is never filled again: the run fills a new one (keeping its art when only the copy is
      // redone); a draft is filled in place. Art the target already holds is never drawn again (the service skips a
      // held hero and held scene layers), so a run that redraws the hero of a version that has one goes to a new
      // version without it, and one that redraws the scene to a new version with none.
      const sel = current();
      const redrawsHero = runOps.includes('hero') && landingHasArt(sel?.assets);
      // The scene lives in the document. A run that draws only what the version misses (a changed character, a failed
      // layer) fills it as it is (the service skips and does not charge what it holds); one that redraws the WHOLE
      // scene (asked for, or the style or the lead character changed) starts from a version without it and without
      // the record of what was drawn.
      const redrawsScene =
        runOps.includes('scene') &&
        landingScenePlan({
          params: doc,
          assets: sel?.assets,
          template,
          styleCast: castMascots.length,
          redrawAll,
        }).full;
      // The document the run reads (the page's) and fills (the target's): without its scene when it is redrawn.
      const runDoc = redrawsScene ? withoutSceneArt(doc) : doc;
      if (redrawsScene && !(await write({ ad_params: runDoc }))) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      if (current()?.key !== editing?.key) return;
      const blank = { params: cloneLandingParams(runDoc), assets: {} };
      const target =
        sel && (isLocked(sel) || redrawsHero || redrawsScene)
          ? fork(
              redrawsHero
                ? blank
                : redrawsScene
                  ? landingSceneRedrawEntry(sel, runDoc, canon)
                  : landingForkEntry(sel, runDoc, canon),
            )
          : mintTarget(blank);
      if (!target) {
        onNotify(t("That couldn't be started. Please try again."));
        return;
      }
      if (redrawsScene) {
        // The editor shows the new version's document (its scene waiting for its new art).
        paramsRef.current = runDoc;
        setParams(runDoc);
      }
      if (!(await target.persisted)) return;
      patchVersion(target.key, runDoc);
      const res = await dispatch(
        enqueueGeneration({
          tenantId,
          placement: 'landing_page',
          prompt: 'landing page',
          mediaType: 'landing_page',
          ad: { landing_page_id: page.id, target_version_at: target.at, ops: [...runOps] },
          // The picked art style and lead character (Auto sends none: the service picks).
          ...(styleId ? { styleId } : {}),
          ...(mascotId ? { mascotId } : {}),
        }),
      ).unwrap();
      if (!res.ok) {
        if (res.needed !== undefined) onBuyCredits(res.needed);
        else onNotify(t("That couldn't be started. Please try again."));
        return;
      }
      setRunTargetKey(target.key);
      // The next run starts on what the filled version holds.
      setOpPicks({});
      setSceneRedrawAll(false);
      if (res.jobId) {
        // Link the run to the page so a reopen (and the library card) follows its progress.
        setSessionJobId(res.jobId);
        patch(page.id, { job_id: res.jobId });
        void write({ job_id: res.jobId });
      }
      void dispatch(fetchOwnerWallets());
    } catch (err) {
      onNotify(startErrorText(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const stop = async (): Promise<void> => {
    if (!job || !active || stopping) return;
    setStopping(true);
    try {
      await dispatch(cancelJob({ tenantId, jobId: job.id })).unwrap();
    } catch {
      onNotify(t("That couldn't be stopped. Please try again."));
    } finally {
      setStopping(false);
    }
  };

  /* ── Preview: rendered by the server shortly after the last edit, over the selected version's art ── */

  const previewAssets = React.useMemo(() => {
    const a = selected?.assets;
    if (!a) return undefined;
    const { errors: _errors, hero_qa: _qa, scene_drawn: _drawn, scene_qa: _sceneQa, ...art } = a;
    return art;
  }, [selected?.assets]);
  const [preview, setPreview] = React.useState<PreviewState>({ html: null, busy: false, failure: null });
  // A cast of no characters shows none at once (the drawn ones stay on the version).
  const previewDoc = React.useMemo(() => landingPreviewParams(params), [params]);
  React.useEffect(() => {
    if (!previewDoc.template_id) return undefined;
    const controller = new AbortController();
    setPreview((p) => (p.busy ? p : { ...p, busy: true }));
    const timer = window.setTimeout(() => {
      void previewLandingPage(tenantId, previewDoc, controller.signal, previewAssets).then((res) => {
        if (controller.signal.aborted) return;
        if (res.ok) {
          const html = res.data.html;
          setPreview((p) =>
            html
              ? { html: withInertLinks(html), busy: false, failure: null }
              : { ...p, busy: false, failure: 'failed' },
          );
          return;
        }
        if (res.aborted) return;
        const failure: PreviewFailure =
          res.status === 400 ? 'unknown-template' : res.status === 404 ? 'unavailable' : 'failed';
        setPreview((p) => ({ ...p, busy: false, failure }));
      });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [tenantId, previewDoc, previewAssets]);

  /* ── The tracking links landing here, and the asset the page was made for ── */

  // Every link landing on the page, its own included: the "Links to this page" section, its stats, and the count the
  // unpublish confirmation warns with.
  const landingLinks = useLandingLinks(tenantId, page.id);
  // "Links to this page" (the header's Links pill): the page's own link, the ads' links landing here, their results.
  // The links sheet too (`?links=<id>`): Back from one of its rows reopens it.
  const linksSheet = useSheetParam(LINKS_SHEET_PARAM);
  const linksOpen = linksSheet.value === page.id;
  const linksHere: LinksHere =
    landingLinks.status === 'loading' || landingLinks.reading
      ? { status: 'checking' }
      : landingLinks.status === 'error'
        ? { status: 'unknown' }
        : { status: 'known', count: landingLinks.links.length };

  // A page made FOR an asset's link ("New landing page…" in its link panel). The durable record is that asset
  // version's link, pointed here at the page's first save (see link-for.ts): the bar reads it back from the links
  // landing here, so a reload, a new tab or another device shows the same. This session's context (sessionStorage) is
  // only a cache: its exact return path, and the asset until its link lands here. One not marked `linked` (the first
  // save's pointing failed, is still in flight, or predates it) is sent on open, again on publish, and from Try again.
  const [openedWith] = React.useState<LinkForContext | null>(() => loadLinkFor(tenantId, page.id));
  const [linkFor, setLinkFor] = React.useState<LinkForContext | null>(openedWith);
  const linkForRef = React.useRef(linkFor);
  linkForRef.current = linkFor;
  const [sendState, setSendState] = React.useState<SendState>('idle');
  const sendBusyRef = React.useRef(false);
  const reloadLinks = landingLinks.reload;
  const sendHere = React.useCallback(
    async (liveUrl: string): Promise<void> => {
      const ctx = linkForRef.current;
      if (!ctx || ctx.linked || sendBusyRef.current) return;
      sendBusyRef.current = true;
      setSendState('sending');
      // Joins the library's call when the first save's pointing is still in flight.
      const ok = await sendLinkHere(tenantId, page.id, ctx, liveUrl);
      sendBusyRef.current = false;
      if (ok) {
        setLinkFor({ ...ctx, linked: true });
        setSendState('idle');
        reloadLinks();
      } else {
        setSendState('failed');
      }
    },
    [tenantId, page.id, reloadLinks],
  );
  const openLiveUrl = page.publication?.state === 'live' ? page.publication.url : '';
  const sentOnOpenRef = React.useRef(false);
  React.useEffect(() => {
    // Syncs with spark-creatives: a made-for asset's link not pointed here yet is sent once as the page opens.
    if (sentOnOpenRef.current) return;
    sentOnOpenRef.current = true;
    void sendHere(openLiveUrl);
  }, [sendHere, openLiveUrl]);

  // The ads' links landing here (the page's own left out): the bar, the header's count, the sheet's rows.
  const adLinks = React.useMemo(
    () => landingLinks.links.filter((link) => !isOwnLink(link, page.id)),
    [landingLinks.links, page.id],
  );
  const linkForOnServer = linkFor ? adLinks.some((link) => isLinkOf(link, linkFor)) : false;
  // A context that was already linked when the page opened, which a settled read no longer shows: the asset's link
  // was moved elsewhere since. It is dropped (never sent here again); a failed or unsettled read never drops it.
  const linkForGone =
    Boolean(openedWith?.linked && linkFor?.linked) &&
    landingLinks.status === 'ready' &&
    !landingLinks.reading &&
    !linkForOnServer;
  React.useEffect(() => {
    // Syncs with sessionStorage: forget a context whose asset's link no longer lands here.
    if (!linkForGone) return;
    clearLinkFor(tenantId, page.id);
    setLinkFor(null);
  }, [linkForGone, tenantId, page.id]);

  // The page's own link minted after the links were read (its first "Get tracking link"): read them again, once per
  // link, so its results join the combined stats.
  const ownLinkSeenRef = React.useRef('');
  const onOwnLink = (link: TrackingLinkState | null): void => {
    const url = link?.trackingUrl ?? '';
    if (!url || ownLinkSeenRef.current === url) return;
    ownLinkSeenRef.current = url;
    if (landingLinks.status !== 'loading' && !landingLinks.links.some((l) => isOwnLink(l, page.id))) {
      landingLinks.reload();
    }
  };
  /** Leave for another Studio page, saving the edits still resting in the debounce first. */
  const goTo = onNavigate
    ? (path: string): void => {
        void flush();
        onNavigate(path);
      }
    : undefined;

  /* ── Publish ── */

  const publication = page.publication;
  const host = live ? primaryHost(live.hosts) : undefined;
  const takenPaths = React.useMemo(
    () => (live ? live.pages.filter((p) => !isPublicationOf(p, publication)).map((p) => p.path) : []),
    [live, publication],
  );
  const [pubFailure, setPubFailure] = React.useState<PublishFailure | null>(null);
  const selectedIsLive = Boolean(
    publication?.state === 'live' &&
      publication.version_at &&
      selected &&
      versionKey(publication.version_at) === selected.key,
  );

  const publish = async (path: string): Promise<void> => {
    const sel = current();
    if (!sel || pubBusy || active || takingRun || submitting) return;
    setPubBusy('publish');
    setPubFailure(null);
    const wasLive = publication?.state === 'live';
    try {
      // The version is published as stored: its document is written once more first (a draft takes the edits on
      // screen; a generated version never changes), and a version minted this session must be saved.
      if (!(await flush())) return;
      if (!(await persistedOf(sel.key))) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      if (!isLocked(sel) && !(await write({ ad_params: paramsRef.current, ad_version_at: sel.created_at }))) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      const res = await publishLandingPage(tenantId, page.id, { path, version_at: sel.created_at });
      if (res.ok) {
        const pub = res.data.publication;
        patch(page.id, { publication: pub });
        onLiveChanged();
        // The other composers' tracking-link destination sheets see the change at once (they cache the brand's pages).
        invalidateLandingDestinations(tenantId);
        landingLinks.reload();
        // The landing app may still refuse it after the checks: the publication then says so (the panel offers Retry).
        if (pub?.state === 'failed') onNotify(t("The page couldn't be published. Please try again."));
        else onNotify(wasLive ? t('The live page is updated.') : t('Your landing page is live.'));
        // A page made for an asset whose link is not pointed here yet (its earlier tries failed): try once more. A
        // link already landing here goes live with the page on its own (spark-creatives builds it from the address).
        if (pub?.state === 'live' && pub.url) void sendHere(pub.url);
        return;
      }
      if (res.aborted) return;
      if (res.error === PATH_TAKEN) setPubFailure({ kind: 'path_taken' });
      else
        setPubFailure({
          kind: 'error',
          text:
            res.status === 400
              ? t("That path can't be used. Choose another one.")
              : res.status === 404
                ? t('That version or page could not be found. Reopen the page and try again.')
                : t("The page couldn't be published. Please try again."),
        });
    } finally {
      setPubBusy(null);
    }
  };

  const unpublish = async (): Promise<void> => {
    if (pubBusy) return;
    setPubBusy('unpublish');
    setPubFailure(null);
    try {
      const res = await unpublishLandingPage(tenantId, page.id);
      if (res.ok) {
        patch(page.id, { publication: res.data.publication });
        onLiveChanged();
        invalidateLandingDestinations(tenantId);
        landingLinks.reload();
        onNotify(t('Your landing page is offline.'));
        return;
      }
      if (res.aborted) return;
      setPubFailure({
        kind: 'error',
        text:
          res.status === 404
            ? t("Publishing isn't available in this environment yet.")
            : t("The page couldn't be unpublished. Please try again."),
      });
    } finally {
      setPubBusy(null);
    }
  };

  const pubBlocked = !template
    ? t('Choose a template first.')
    : active || takingRun || submitting
      ? t('Wait for Lumio to finish.')
      : !selected
        ? t('Setting up the first version…')
        : null;

  // A never-published page is suggested a path from what it promotes (one no other live page of the brand uses).
  const featuredForSlug = params.featured;
  const subjectForSlug = params.subject;
  const defaultSlug = React.useMemo(
    () =>
      freeLandingSlug(
        landingDefaultSlug({ featured: featuredForSlug, subject: subjectForSlug }, template?.game),
        takenPaths,
      ),
    [featuredForSlug, subjectForSlug, template?.game, takenPaths],
  );
  const publishing = usePublishControls({
    publication,
    host,
    hostsKnown: live !== null || liveFailed,
    hostsFailed: liveFailed,
    takenPaths,
    defaultSlug,
    busy: pubBusy,
    failure: pubFailure,
    blockedText: pubBlocked,
    selectedIsLive,
    selectedLocked: isLocked(selected),
    noHero: Boolean(template?.hero) && !landingHeroUrl(selected ? { params, assets: selected.assets } : undefined),
    onPublish: (path) => void publish(path),
    onUnpublish: () => void unpublish(),
    onPathEdit: () => setPubFailure(null),
    onNotify,
    linksHere,
    onUnpublishAsk: landingLinks.reload,
  });

  /* ── Derived view ── */

  const lastRunFailed = !active && job?.status === 'failed' && !isLocked(selected);
  const stopped = lastRunFailed && isCancelled(job?.error);
  const opErrors = !active && selected?.assets.errors ? Object.keys(selected.assets.errors) : [];
  const generatedHero = selected?.assets.hero_url ?? '';
  const heroNeedsReview = landingHeroNeedsReview(selected ? { params, assets: selected.assets } : undefined);
  const pageName = name.trim() || page.name || t('Untitled landing page');

  const pickerVersions: VersionPickerItem[] = versions.map((v) => ({
    key: v.key,
    createdAt: Date.parse(v.created_at),
    locked: isLocked(v),
    generating: v.key === runKey,
    needsReview: landingHeroNeedsReview(v),
    // The picker shows what the hero check actually found under the flagged version (the same line as the hero badge).
    reviewNote: landingHeroNeedsReview(v) ? heroReviewReason(v.assets.hero_qa?.reason, t) : undefined,
  }));

  const waitingText =
    templates.status === 'error'
      ? t("The templates couldn't be loaded, so there is nothing to preview yet.")
      : templates.status === 'ready' && templates.list.length === 0
        ? t('No template is available yet.')
        : null;
  const featured = params.featured;
  // The section is named after what the page promotes, like the other composers' subject sections.
  const subjectTitle = featured?.type === 'game' ? t('Games') : t('Promotion');
  const featuredSummary = featured
    ? featured.items
        .map((item) => item.name || item.headline || '')
        .filter(Boolean)
        .join(', ') || subjectTitle
    : t('Your welcome offer');
  const sceneLayers = sceneLayerCount(params.scene);
  const sceneLayersSummary = sceneLayers > 0 ? t('{{n}} layers', { n: sceneLayers }) : t('Not generated yet');
  const castPicked = pickedCast(params.cast, castMascots);
  const castSummary = isCastNone(params.cast)
    ? t('No characters')
    : castPicked.length > 0
      ? castPicked.map((id) => castMascots.find((m) => m.id === id)?.name.trim() || t('Unnamed character')).join(', ')
      : t('Automatic characters');
  const sceneSummary =
    castMascots.length > 0
      ? t('{{layers}} · {{cast}}', { layers: sceneLayersSummary, cast: castSummary })
      : sceneLayersSummary;
  /** A new cast for the scene's characters (developer control). The page keeps only the characters its version holds
   *  for the new cast (none for No characters, at once); Scene art then starts ticked exactly when a character is
   *  missing and draws (and charges) only those. */
  const onCast = (next: string[]): void => {
    if (railLocked || castEqual(paramsRef.current.cast, next)) return;
    const assets = current()?.assets;
    edit((cur) =>
      castEqual(cur.cast, next)
        ? cur
        : {
            ...cur,
            cast: next,
            scene: {
              ...cur.scene,
              characters: landingCastCharacters(
                next,
                assets,
                castMascots.length,
                cur.scene.characters,
                landingFeaturedGameArt(cur),
              ),
            },
          },
    );
    // Scene art's tick follows what the version now misses.
    setOpPicks(({ scene: _scene, ...rest }) => rest);
    setSceneRedrawAll(false);
  };
  const opLabel = (op: LandingOp): string =>
    op === 'hero'
      ? t('Hero art')
      : op === 'scene'
        ? sceneCanFill && !redrawAll
          ? t('Scene art, {{n}} missing', { n: scenePlan.missing.length })
          : t('Scene art')
        : op === 'lettering'
          ? t('Lettering, offer and button')
          : t('Copy, every language');
  const sceneBgs = scenePlan.keys.filter(isSceneBackgroundKey).length;
  const sceneCuts = scenePlan.keys.length - sceneBgs;

  const handleClose = (): void => {
    void flush();
    onClose();
  };

  // The made-for bar over the composer: each ad whose tracking link lands here (read back from spark-creatives, so the
  // same in any tab), as a deep link to its composer, plus this session's made-for asset while its link is not shown
  // landing here yet (not pointed yet, or the read not settled); then where the links stand.
  const liveUrl = publication?.state === 'live' ? publication.url : '';
  const madeForAssets: MadeForAsset[] = adLinks.map((link) => ({
    key: link.id,
    name: link.name || t('Untitled'),
    path: assetLinkPath(link.kind, link.creative_id, linkFor),
  }));
  if (linkFor && !linkForOnServer && !linkForGone) {
    madeForAssets.unshift({
      key: `for:${linkFor.creativeId}:${linkFor.versionKey}`,
      name: linkFor.name,
      path: assetLinkPath(linkFor.kind, linkFor.creativeId, linkFor),
    });
  }
  const madeForState: MadeForState =
    sendState === 'sending' ? 'sending' : sendState === 'failed' ? 'failed' : liveUrl ? 'live' : 'draft';
  const linkForBar =
    madeForAssets.length > 0 ? (
      <MadeForBar
        assets={madeForAssets}
        state={madeForState}
        onOpen={goTo}
        onRetry={() => void sendHere(liveUrl)}
      />
    ) : null;

  // The page's own tracking link, copied from the Links sheet once the page is live.
  const ownLinkSection =
    publication?.state === 'live' && publication.url ? (
      <S.Section>
        <S.SectionTitle text="Unique tracking link" />
        <S.Hint text="This page's own link, separate from your ads' tracking links. Share it yourself anywhere (a post, a message, an email): the sign-ups it brings are credited to this page." />
        <TrackingLink
          key={publication.url}
          kind="landing_page"
          creativeId={page.id}
          versionKey="page"
          promotions={promotionsForTracking(params.subject)}
          poolItems={items}
          brandDomain={brandDomain}
          adName={pageName}
          tenantId={tenantId}
          fixedLandingPage={{ id: page.id, url: publication.url }}
          onLinkChange={onOwnLink}
        />
      </S.Section>
    ) : null;

  // The language the page opens in (an endonym, data: never translated); '' until it is known.
  const opensIn = adLanguageLabel(params.default_language || siteDefault);
  const status = active ? (
    <>
      {job?.total ? (
        <S.FootText
          text="Generating · {{done}} of {{total}}"
          placeholders={{ done: job.completed ?? 0, total: job.total }}
        />
      ) : (
        <S.FootText text="Starting…" />
      )}
      <S.ProgressTrack aria-hidden="true">
        <S.ProgressFill $pct={job?.total ? ((job.completed ?? 0) / job.total) * 100 : 0} />
      </S.ProgressTrack>
    </>
  ) : stopped ? (
    <S.FootText text="Stopped. Your credits were refunded." />
  ) : !template ? (
    <S.Hint text="Choose a template to start." />
  ) : lockedView ? (
    <S.Hint text="This version is generated. Start a new version from it to change it." />
  ) : nothingToDraw ? (
    <S.Hint text="This version already holds everything you ticked." />
  ) : (
    <>
      {template.scene ? (
        <S.Hint text="Lumio draws the scene in your art style and writes the copy in every language of your website." />
      ) : template.hero ? (
        <S.Hint text="Lumio draws the hero in your art style and writes the copy in every language of your website." />
      ) : (
        <S.Hint text="Lumio writes the copy in every language of your website." />
      )}
      {opensIn && params.languages.length > 1 ? (
        <S.Hint text="The page opens in {{language}}." placeholders={{ language: opensIn }} />
      ) : null}
    </>
  );

  // Some lettering no longer reads the copy (or a language has none yet): the page shows the live text there.
  const letteringHint =
    !active && !lockedView && letteringStale && hasLettering ? (
      opOn('lettering') ? (
        <S.Hint text="The lettering redraws on Generate. Until then the page shows the text as it is." />
      ) : (
        <S.Hint text="The page shows the text as it is until the lettering is drawn again." />
      )
    ) : null;

  const rail = (
    <S.KitRail>
      <S.LockableRailScroll $locked={lockedView} onClick={lockedNudge.onRailClick}>
        <IngredientSection
          id="lp-subject"
          title={subjectTitle}
          icon="megaphone"
          summary={featuredSummary}
          open={open === 'subject'}
          onToggle={() => toggle('subject')}
        >
          <S.Stack>
            {featured ? (
              <S.FeaturedList>
                {featured.items.map((item) => (
                  <S.SubjectCard key={item.id}>
                    {item.image_url ? <S.SubjectThumb src={item.image_url} alt="" /> : null}
                    <S.SubjectCopy>
                      <S.CopyHeadline raw text={item.headline || item.name} />
                      {item.subtitle ? <S.CopySub raw text={item.subtitle} /> : null}
                    </S.SubjectCopy>
                  </S.SubjectCard>
                ))}
              </S.FeaturedList>
            ) : params.subject.id ? (
              <S.SubjectCard>
                {params.subject.image_url ? <S.SubjectThumb src={params.subject.image_url} alt="" /> : null}
                <S.SubjectCopy>
                  <S.CopyHeadline raw text={params.subject.headline || params.subject.name} />
                  {params.subject.subtitle ? <S.CopySub raw text={params.subject.subtitle} /> : null}
                </S.SubjectCopy>
              </S.SubjectCard>
            ) : (
              <S.Hint text="The page carries your welcome offer." />
            )}
            <S.Hint text="Chosen when the page was created. Its button always claims your welcome offer, and Lumio writes the copy and draws the art around what the page promotes." />
          </S.Stack>
        </IngredientSection>

        <AdLanguageSection
          id="lp-language"
          open={open === 'language'}
          onToggle={() => toggle('language')}
          value={params.default_language}
          siteLanguages={siteLanguagesState}
          disabled={railLocked}
          onChange={onOpeningLanguage}
          hint={
            <SectionHint
              text="The page opens in this language. Visitors can still switch to your website's other languages."
              tag="span"
            />
          }
        />

        <IngredientSection
          id="lp-art-style"
          title={t('Art style')}
          icon="palette"
          summary={styleRun.styleSummary}
          open={open === 'art-style'}
          onToggle={() => toggle('art-style')}
        >
          <S.ArtStyleBody>
            <AdArtStyleSection
              tenantId={tenantId}
              run={styleRun}
              disabled={railLocked}
              onPickStyle={(id) => {
                if (!railLocked) styleRun.pickStyle(id);
              }}
            />
            <AdDrawnWith artStyle={selected?.art_style} />
          </S.ArtStyleBody>
        </IngredientSection>

        {styleRun.hasCast ? (
          <IngredientSection
            id="lp-character"
            title={t('Character')}
            icon="user"
            summary={styleRun.characterSummary(styleRun.mascotPick)}
            open={open === 'character'}
            onToggle={() => toggle('character')}
          >
            <AdCharacterSection
              tenantId={tenantId}
              value={styleRun.mascotPick}
              onChange={(pick) => {
                if (!railLocked) styleRun.pickMascot(pick);
              }}
              disabled={railLocked}
              cast={styleRun.cast}
            />
          </IngredientSection>
        ) : null}

        {devDebug && template?.scene ? (
          <IngredientSection
            id="lp-scene"
            title={t('Scene')}
            icon="image"
            summary={sceneSummary}
            open={open === 'scene'}
            onToggle={() => toggle('scene')}
          >
            <S.Stack>
              {castMascots.length > 0 ? (
                <SceneCast
                  mascots={castMascots}
                  value={params.cast}
                  around={immersive ? 'game' : 'offer'}
                  disabled={railLocked}
                  onChange={onCast}
                />
              ) : null}
              <SceneLayers
                scene={params.scene}
                parts={template.scene_parts}
                symbols={immersive}
                disabled={railLocked}
                onChange={(fn) => edit((cur) => ({ ...cur, scene: fn(cur.scene) }))}
              />
            </S.Stack>
          </IngredientSection>
        ) : null}

        {devDebug && template?.hero ? (
          <IngredientSection
            id="lp-hero"
            title={t('Hero image')}
            icon="image"
            summary={
              params.hero.image_url
                ? t('Your image')
                : generatedHero
                  ? heroNeedsReview
                    ? t('Drawn by Lumio · Needs review')
                    : t('Drawn by Lumio')
                  : t('Not drawn yet')
            }
            open={open === 'hero'}
            onToggle={() => toggle('hero')}
          >
            <S.Stack>
              {params.hero.image_url ? (
                <>
                  <S.HeroThumb src={params.hero.image_url} alt="" />
                  <Button
                    variant="text"
                    label={t("Use Lumio's hero instead")}
                    disabled={railLocked}
                    onClick={() => edit((cur) => ({ ...cur, hero: { ...cur.hero, image_url: '' } }))}
                  />
                </>
              ) : generatedHero ? (
                <>
                  <S.HeroThumb src={generatedHero} alt="" />
                  {heroNeedsReview ? (
                    <S.HeroReviewBadge
                      text="Needs review"
                      title={heroReviewReason(selected?.assets.hero_qa?.reason, t)}
                    />
                  ) : null}
                </>
              ) : (
                <S.Hint text="Lumio draws the hero in your art style when you generate." />
              )}
              <Button
                variant="outline"
                icon="image"
                label={t('Use art from your promotions')}
                disabled={railLocked || artItems.length === 0}
                onClick={() => {
                  setHeroPick(null);
                  setPickingHero(true);
                }}
              />
              <Field
                label={t('Your own image URL')}
                value={params.hero.image_url}
                onChange={(value) => edit((cur) => ({ ...cur, hero: { ...cur.hero, image_url: value } }))}
                placeholder="https://"
                disabled={railLocked}
              />
              <Field
                label={t('Video URL (optional)')}
                value={params.hero.video_url}
                onChange={(value) => edit((cur) => ({ ...cur, hero: { ...cur.hero, video_url: value } }))}
                placeholder="https://"
                disabled={railLocked}
              />
              {template.hero_aspect ? (
                <S.Hint
                  text="This template is drawn for a {{aspect}} image."
                  placeholders={{ aspect: template.hero_aspect }}
                />
              ) : null}
            </S.Stack>
          </IngredientSection>
        ) : null}
      </S.LockableRailScroll>

      <S.RailFoot>
        <S.RailStatus>{status}</S.RailStatus>
        {letteringHint}
        {active ? (
          <Button
            variant="outline"
            icon="stop"
            fullWidth
            label={stopping ? t('Stopping…') : t('Stop')}
            disabled={stopping}
            onClick={() => void stop()}
          />
        ) : lockedView ? (
          <NewVersionLink nudge={lockedNudge.nudge} disabled={formLocked} onClick={newVersion} />
        ) : (
          <>
            {devDebug && available.length > 0 ? (
              <S.OpsList role="group" aria-label={t('What Lumio generates')}>
                {available.map((op) => (
                  <Checkbox
                    key={op}
                    checked={opOn(op)}
                    label={opLabel(op)}
                    disabled={submitting}
                    onChange={(on) => setOpPicks((cur) => ({ ...cur, [op]: on }))}
                  />
                ))}
              </S.OpsList>
            ) : null}
            {devDebug && ops.includes('scene') && sceneCanFill && !picksChanged ? (
              <Checkbox
                checked={sceneRedrawAll}
                label={t('Redraw the whole scene')}
                disabled={submitting}
                onChange={setSceneRedrawAll}
              />
            ) : null}
            {devDebug && ops.includes('scene') ? (
              scenePrice.background !== undefined && scenePrice.cutout !== undefined ? (
                <S.Hint
                  text="Scene art: {{bgs}} backgrounds and up to {{cuts}} cut-outs. Credits per image: {{bg}} a background, {{cut}} a cut-out."
                  placeholders={{
                    bgs: sceneBgs,
                    bg: scenePrice.background,
                    cuts: sceneCuts,
                    cut: scenePrice.cutout,
                  }}
                />
              ) : (
                <S.Hint
                  text="Scene art is priced per image: {{bgs}} backgrounds and up to {{cuts}} cut-outs."
                  placeholders={{ bgs: sceneBgs, cuts: sceneCuts }}
                />
              )
            ) : null}
            <GenerateButton
              label={t('Generate')}
              cost={cost}
              disabled={!canGenerate}
              onClick={() => {
                setRailTab('output');
                void startRun();
              }}
            />
          </>
        )}
      </S.RailFoot>
    </S.KitRail>
  );

  const output = (
    <S.KitOutput>
      <S.OutputScroll>
        {lastRunFailed && !stopped ? (
          <Callout
            tone="danger"
            title={t("This landing page couldn't be generated")}
            text={job?.error || t('Your credits were refunded. Please try again.')}
          />
        ) : null}
        {opErrors.length > 0 ? (
          <Callout
            tone="warning"
            text={t('Some of it could not be generated and its credits were refunded. Generate again to retry it.')}
          />
        ) : null}
        {active ? <S.NoteText text="The page updates as Lumio's work lands." /> : null}

        <S.PreviewArea>
          <LandingPreview
            html={preview.html}
            busy={preview.busy}
            failure={preview.failure}
            waitingText={waitingText}
            immersive={immersive}
            addressBar={publishing.addressBar}
            actions={publishing.actions}
            notice={publishing.notice}
          />
        </S.PreviewArea>
      </S.OutputScroll>
    </S.KitOutput>
  );

  return (
    <Sheet
      onClose={handleClose}
      ariaLabel={t('Landing page')}
      maxWidth="1100px"
      maxHeight="100%"
      header={
        <S.Header>
          <S.HeaderLeft>
            <S.TitleWrap>
              <S.TitleInput
                ref={nameRef}
                value={name}
                size={Math.max((name || t('Name this landing page')).length + 1, 4)}
                onChange={(e) => onName(e.target.value)}
                placeholder={t('Name this landing page')}
                aria-label={t('Landing page name')}
              />
              <S.TitleEditIcon type="button" aria-label={t('Edit name')} onClick={() => nameRef.current?.focus()}>
                <Icon name="edit" size={14} />
              </S.TitleEditIcon>
            </S.TitleWrap>
            <VersionPicker
              versions={pickerVersions}
              value={selected?.key}
              onChange={onSelectVersion}
              disabled={submitting || pubBusy !== null}
            />
            {/* The page's links (its own, the ads' landing here), beside the Stats pill (their combined results). */}
            <L.LinksToggle type="button" aria-label={t('Links to this page')} onClick={() => linksSheet.open(page.id)}>
              <Icon name="megaphone" size={15} />
              {adLinks.length > 0 ? (
                <>
                  <L.LinksToggleLabel text="Links · {{n}}" placeholders={{ n: adLinks.length }} />
                  <L.LinksToggleCount raw text={String(adLinks.length)} />
                </>
              ) : (
                <L.LinksToggleLabel text="Links" />
              )}
            </L.LinksToggle>
            <StatsPill onClick={() => statsSheet.open(page.id)} />
          </S.HeaderLeft>
        </S.Header>
      }
    >
      {linkForBar}
      {showPicker ? (
        <LandingTemplatePicker
          tenantId={tenantId}
          templates={templates}
          params={params}
          selectedId={template?.id ?? ''}
          canCancel={false}
          disabled={railLocked}
          onPick={pickTemplate}
          onCancel={() => undefined}
        />
      ) : (
        <>
          <ComposerRailTabs value={railTab} onChange={setRailTab} outputLabel="Landing page" />
          <L.SplitStage $underBar={linkForBar !== null}>
            <S.Split $mobileTab={railTab} $railEnter={railEnter}>
              {rail}
              {output}
            </S.Split>
          </L.SplitStage>
        </>
      )}

      {linksOpen ? (
        <LandingLinks
          pageId={page.id}
          read={landingLinks}
          onOpen={goTo}
          madeFor={linkFor}
          ownLink={ownLinkSection}
          onClose={linksSheet.close}
        />
      ) : null}

      {statsOpen ? (
        <AssetStatsSheet
          kind="landing_page"
          creativeId={page.id}
          name={name}
          landingPath={page.publication?.path}
          onClose={statsSheet.close}
        />
      ) : null}

      {pickingHero ? (
        <PoolPickerSheet
          title={t('Choose the hero image')}
          poolItems={artItems}
          selectedIds={heroPick ? [heroPick] : []}
          labelOf={promotionName}
          onToggle={(id) => setHeroPick((cur) => (cur === id ? null : id))}
          onClose={() => setPickingHero(false)}
          emptyText={t('This brand has no promotion art yet.')}
          hint={t('Pick one. Its art becomes the hero of the page instead of Lumio drawing one.')}
          footer={
            <FooterRow>
              <SelCount text="{{n}} selected" placeholders={{ n: heroPick ? 1 : 0 }} />
              <FooterActions>
                <Button variant="text" label={t('Cancel')} onClick={() => setPickingHero(false)} />
                <Button variant="primary" label={t('Use this image')} onClick={confirmHero} disabled={!heroPick} />
              </FooterActions>
            </FooterRow>
          }
        />
      ) : null}
    </Sheet>
  );
};

export default LandingPageComposer;
